import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ============================================
// PAGES API
// ============================================
export const pagesApi = {
  // Get all published pages (for public site)
  async getPublished() {
    const { data, error } = await supabase
      .from('pages')
      .select('*')
      .eq('published', true)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  // Get all pages (for admin)
  async getAll() {
    const { data, error } = await supabase
      .from('pages')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  // Get single page by slug with its sections
  async getBySlug(slug) {
    const { data: page, error: pageError } = await supabase
      .from('pages')
      .select('*')
      .eq('slug', slug)
      .single();
    if (pageError) throw pageError;

    const { data: sections, error: sectionsError } = await supabase
      .from('page_sections')
      .select('*')
      .eq('page_id', page.id)
      .order('display_order', { ascending: true });
    if (sectionsError) throw sectionsError;

    return { ...page, sections: sections || [] };
  },

  // Get published page by slug with its published sections
  async getPublishedBySlug(slug) {
    const { data: page, error: pageError } = await supabase
      .from('pages')
      .select('*')
      .eq('slug', slug)
      .eq('published', true)
      .single();
    if (pageError) throw pageError;

    const { data: sections, error: sectionsError } = await supabase
      .from('page_sections')
      .select('*')
      .eq('page_id', page.id)
      .eq('published', true)
      .order('display_order', { ascending: true });
    if (sectionsError) throw sectionsError;

    return { ...page, sections: sections || [] };
  },

  // Create page
  async create(page) {
    const { data, error } = await supabase
      .from('pages')
      .insert([page])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Update page
  async update(id, updates) {
    const { data, error } = await supabase
      .from('pages')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Delete page
  async delete(id) {
    const { error } = await supabase
      .from('pages')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  // Toggle publish status
  async togglePublish(id, published) {
    return this.update(id, { published });
  }
};

// ============================================
// PAGE SECTIONS API
// ============================================
export const sectionsApi = {
  // Get sections for a page
  async getByPageId(pageId) {
    const { data, error } = await supabase
      .from('page_sections')
      .select('*')
      .eq('page_id', pageId)
      .order('display_order', { ascending: true });
    if (error) throw error;
    return data;
  },

  // Create section
  async create(section) {
    const { data, error } = await supabase
      .from('page_sections')
      .insert([section])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Update section
  async update(id, updates) {
    const { data, error } = await supabase
      .from('page_sections')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Delete section
  async delete(id) {
    const { error } = await supabase
      .from('page_sections')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  // Reorder sections
  async reorder(sections) {
    for (const section of sections) {
      await supabase
        .from('page_sections')
        .update({ display_order: section.display_order })
        .eq('id', section.id);
    }
  }
};

// ============================================
// NOTES API
// ============================================
export const notesApi = {
  // Get all published notes (for public site)
  async getPublished() {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('published', true)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  // Get all notes (for admin)
  async getAll() {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  // Get single note by slug
  async getBySlug(slug) {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('slug', slug)
      .single();
    if (error) throw error;
    return data;
  },

  // Get single note by id
  async getById(id) {
    const { data, error } = await supabase
      .from('notes')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  // Create note
  async create(note) {
    const { data, error } = await supabase
      .from('notes')
      .insert([note])
      .select()
      .single();
    if (error) {
      console.error('Create note error:', error);
      throw new Error(error.message || error.details || 'Failed to create note');
    }
    return data;
  },

  // Update note
  async update(id, updates) {
    const { data, error } = await supabase
      .from('notes')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) {
      console.error('Update note error:', error);
      throw new Error(error.message || error.details || 'Failed to update note');
    }
    return data;
  },

  // Delete note
  async delete(id) {
    const { error } = await supabase
      .from('notes')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  // Toggle publish status
  async togglePublish(id, published) {
    return this.update(id, { published });
  }
};

// ============================================
// RESEARCH PROJECTS API
// ============================================
export const researchApi = {
  // Get all published projects (for public site)
  async getPublished() {
    const { data, error } = await supabase
      .from('research_projects')
      .select('*')
      .eq('published', true)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  // Get all projects (for admin)
  async getAll() {
    const { data, error } = await supabase
      .from('research_projects')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  // Get single project by slug
  async getBySlug(slug) {
    const { data, error } = await supabase
      .from('research_projects')
      .select('*')
      .eq('slug', slug)
      .single();
    if (error) throw error;
    return data;
  },

  // Get single project by id
  async getById(id) {
    const { data, error } = await supabase
      .from('research_projects')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  // Create project
  async create(project) {
    const { data, error } = await supabase
      .from('research_projects')
      .insert([project])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Update project
  async update(id, updates) {
    const { data, error } = await supabase
      .from('research_projects')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Delete project
  async delete(id) {
    const { error } = await supabase
      .from('research_projects')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  // Toggle publish status
  async togglePublish(id, published) {
    return this.update(id, { published });
  }
};

// ============================================
// POLICIES API
// ============================================
export const policiesApi = {
  // Get all published policies (for public site)
  async getPublished() {
    const { data, error } = await supabase
      .from('policies')
      .select('*')
      .eq('published', true)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data;
  },

  // Get all policies (for admin)
  async getAll() {
    const { data, error } = await supabase
      .from('policies')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data;
  },

  // Get single policy by slug
  async getBySlug(slug) {
    const { data, error } = await supabase
      .from('policies')
      .select('*')
      .eq('slug', slug)
      .single();
    if (error) throw error;
    return data;
  },

  // Get single policy by id
  async getById(id) {
    const { data, error } = await supabase
      .from('policies')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  // Update policy
  async update(id, updates) {
    const { data, error } = await supabase
      .from('policies')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // Toggle publish status
  async togglePublish(id, published) {
    return this.update(id, { published });
  }
};

// ============================================
// SITE SETTINGS API
// ============================================
export const settingsApi = {
  // Get all settings
  async getAll() {
    const { data, error } = await supabase
      .from('site_settings')
      .select('*');
    if (error) throw error;
    // Convert array to object for easy access
    return data?.reduce((acc, item) => {
      acc[item.key] = item.value;
      return acc;
    }, {}) || {};
  },

  // Get single setting
  async get(key) {
    const { data, error } = await supabase
      .from('site_settings')
      .select('value')
      .eq('key', key)
      .single();
    if (error) return null;
    return data?.value;
  },

  // Update or create setting
  async set(key, value) {
    const { data: existing } = await supabase
      .from('site_settings')
      .select('id')
      .eq('key', key)
      .single();

    if (existing) {
      const { error } = await supabase
        .from('site_settings')
        .update({ value })
        .eq('key', key);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('site_settings')
        .insert([{ key, value }]);
      if (error) throw error;
    }
  }
};

// ============================================
// STORAGE API
// ============================================
export const storageApi = {
  async uploadImage(file, bucket = 'images') {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return publicUrl;
  },

  async deleteImage(url, bucket = 'images') {
    const fileName = url.split('/').pop();
    const { error } = await supabase.storage
      .from(bucket)
      .remove([fileName]);
    if (error) throw error;
  },

  async listImages(bucket = 'images') {
    const { data, error } = await supabase.storage
      .from(bucket)
      .list('', {
        limit: 100,
        sortBy: { column: 'created_at', order: 'desc' }
      });
    
    if (error) throw error;
    
    // Filter out folders and get public URLs
    const images = (data || [])
      .filter(file => file.name && !file.name.endsWith('/'))
      .map(file => {
        const { data: { publicUrl } } = supabase.storage
          .from(bucket)
          .getPublicUrl(file.name);
        return {
          name: file.name,
          url: publicUrl,
          size: file.metadata?.size || 0,
          created_at: file.created_at
        };
      });
    
    return images;
  }
};

// ============================================
// AUTH API
// ============================================
export const authApi = {
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    if (error) throw error;
  },

  async getSession() {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  async getUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  }
};
